import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/node';

import Input from '../../atoms/input-email';
import InputWrapper from '../input-wrapper';
import Button from '../../atoms/button';
import IconCircleArrow from '../../atoms/icon-circle-arrow';
import trackEvent from '../../utils/tracking';

import { subscribeToEmails } from '../../pages/api';

const Wrapper = styled.div`
  position: relative;
  margin: 4rem 2rem;
  min-height: 6.5rem;
`;

const EmailInputWrapper = styled(InputWrapper)`
  > label {
    position: absolute;
    top: -1rem;
    letter-spacing: 0.24em;
    text-transform: uppercase;
  }
`;

const EmailInput = styled(Input)`
  outline: 0;
  padding: 0;
  width: 100%;
`;

const Submit = styled(Button)`
  border: 0;
  height: 32px;
  overflow: visible;
  position: absolute;
  right: 0;
  top: 6px;
  width: 32px;
  z-index: ${({ theme }) => theme.layers.backstage + 1};

  svg {
    fill: transparent;
    height: 27px;
    stroke-width: 4;
    stroke: #fff;
    width: 27px;

    path {
      transform: translate3d(0, 0, 0);
    }
  }

  :hover:not(:disabled) {
    svg {
      path {
        animation: arrow-head;
        animation-duration: ${(props) => props.theme.animation.slow};
        animation-timing-function: ${(props) => props.theme.animation.easeMove};
        animation-delay: 0s;
        animation-iteration-count: 1;
        animation-direction: normal;
        animation-fill-mode: forwards;
        animation-play-state: running;
      }
    }
  }

  :disabled {
    opacity: 1;
  }
`;

const ErrorMsg = styled.span`
  color: ${(props) => props.theme.color.brandError};
  display: block;
  font-family: ${(props) => props.theme.font.sans};
  font-size: 1.2rem;
  margin-top: 0.25rem;
  width: 100%;
`;

const SubscribeListInput = (props) => (
  <Formik
    initialValues={{ email: '', source: 'Modal' }}
    validateOnBlur={false}
    validateOnChange={false}
    validationSchema={
      Yup.object().shape({
        email: Yup.string().email('Please enter a valid Email Address').required('Email is required')
      })
    }
    onSubmit={async (model, actions) => {
      actions.setSubmitting(true);

      trackEvent({
        eventCategory: 'Email acquisition',
        eventAction: 'Modal subscribe',
        eventLabel: 'Attempt'
      });

      await subscribeToEmails({
        body: {
          subscriber: {
            ...model,
            first_name: '',
            last_name: ''
          }
        }
      })
        .then(() => {
          actions.setSubmitting(false);
          actions.resetForm();
          props.customSubmit(true);

          trackEvent({
            eventCategory: 'Email acquisition',
            eventAction: 'Modal subscribe',
            eventLabel: 'Success'
          });

          global.document.cookie = 'subscribed_to_emails=1; path=/';
        })
        .catch((err) => {
          props.customSubmit(false);
          trackEvent({
            eventCategory: 'Email acquisition',
            eventAction: 'Modal subscribe',
            eventLabel: 'Failure'
          });

          Sentry.withScope((scope) => {
            scope.setContext('model', model);
            Sentry.captureException(err);
          });
          console.error(err);
        });
    }}
  >
    {({ isSubmitting }) => (
      <Form>
        <Wrapper>
          <Field name="email">
            {({ field, meta }) => (
              <EmailInputWrapper
                for={'subscribe-list-email'}
                label="Email Address"
                labelLike="label-1"
                labelColor="white"
                labelFocusColor="white"
              >
                {meta.touched && meta.error && <ErrorMsg id="subscribe-list-error" role="alert">{meta.error}</ErrorMsg>}
                <EmailInput
                  id="subscribe-list-email"
                  type="email"
                  placeholder=" "
                  component={Input}
                  underline
                  inverted
                  {...field}
                  aria-describedby={meta.touched && meta.error ? 'subscribe-list-error' : null}
                  autoComplete="email"
                />
              </EmailInputWrapper>
            )}
          </Field>
          <Submit aria-label="submit join subscribe list" type="submit" isIcon disabled={isSubmitting}><IconCircleArrow /></Submit>
        </Wrapper>
      </Form>
    )}
  </Formik>
);

SubscribeListInput.propTypes = {
  customSubmit: PropTypes.func.isRequired
};

export default SubscribeListInput;
